import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../foundation/altzero_base/base/components/ui/card';
import { Badge } from '../../../foundation/altzero_base/base/components/ui/badge';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { 
  Star, 
  TrendingUp, 
  TrendingDown,
  ChevronUp,
  ChevronDown,
  Filter,
  Settings,
  BarChart3,
  Leaf,
  Calendar,
  DollarSign
} from 'lucide-react';
import { Offer, PricingComponent, PRICING_COMPONENTS } from '../types/offerTypes';
import { formatTermLabel } from '../lib/termLabel';
import { formatEnergyRate } from '../lib/ratePrecision';

interface OfferComparisonProps {
  offers: Offer[];
  onOfferSelect: (offerId: string) => void;
}

const OfferComparison: React.FC<OfferComparisonProps> = ({
  offers,
  onOfferSelect,
}) => {
  const [sortBy, setSortBy] = useState<'total_cost' | 'savings' | 'retailer_name'>('total_cost');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatRate = (rate: number, unit: string) => {
    if (unit.includes('c/')) {
      return `${rate.toFixed(1)}c/${unit.split('/')[1]}`;
    }
    // Full quoted precision — see lib/ratePrecision; 9.9076 must not print as 9.91.
    return `${formatEnergyRate(rate)} ${unit}`;
  };

  const formatPercent = (percent: number) => {
    return `${percent > 0 ? '+' : ''}${percent.toFixed(1)}%`;
  };

  // Get pricing component value for an offer
  const getPricingValue = (offer: Offer, component: PricingComponent): number | null => {
    const line = offer.offerLines.find(line => line.component === component);
    return line ? line.rate : null;
  };

  // Sort offers
  const sortedOffers = [...offers].sort((a, b) => {
    let aValue: number, bValue: number;
    
    switch (sortBy) {
      case 'total_cost':
        aValue = a.estimatedAnnualCost || 0;
        bValue = b.estimatedAnnualCost || 0;
        break;
      case 'savings':
        aValue = a.estimatedSavingsPercent || 0;
        bValue = b.estimatedSavingsPercent || 0;
        break;
      case 'retailer_name':
        return sortDirection === 'asc' 
          ? a.retailerName.localeCompare(b.retailerName)
          : b.retailerName.localeCompare(a.retailerName);
      default:
        aValue = 0;
        bValue = 0;
    }

    return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const handleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const toggleRowExpansion = (offerId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(offerId)) {
      newExpanded.delete(offerId);
    } else {
      newExpanded.add(offerId);
    }
    setExpandedRows(newExpanded);
  };

  const recommendedOffer = offers.find(offer => offer.isRecommended);
  const keyComponents: PricingComponent[] = ['peak_rate', 'offpeak_rate', 'daily_supply'];

  if (offers.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No offers to compare
          </h3>
          <p className="text-gray-500">
            Add at least two offers to see the comparison matrix.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Comparison Header */}
      <Card className="border border-primary/10">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 p-2 rounded-lg">
                <BarChart3 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl font-semibold">
                  Retailer Comparison
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Compare {offers.length} energy offers side by side
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Filter className="w-4 h-4 mr-2" />
                Filter
              </Button>
              <Button variant="outline" size="sm">
                <Settings className="w-4 h-4 mr-2" />
                Customize
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          {/* Comparison Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-700 min-w-[120px]">
                    Retailer
                  </th>
                  {sortedOffers.map((offer) => (
                    <th key={offer.id} className="text-center py-3 px-4 min-w-[140px]">
                      <div className="space-y-2">
                        <div className="flex items-center justify-center gap-2">
                          <span className="font-semibold text-gray-900">
                            {offer.retailerName}
                          </span>
                          {offer.isRecommended && (
                            <Badge className="bg-green-100 text-green-800 text-xs">
                              <Star className="w-3 h-3 mr-1 fill-current" />
                              BEST
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {offer.offerType} • {formatTermLabel(offer.termMonths)}
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              
              <tbody>
                {/* Key Pricing Components */}
                {keyComponents.map((component) => {
                  const componentInfo = PRICING_COMPONENTS.find(c => c.id === component);
                  if (!componentInfo) return null;

                  return (
                    <tr key={component} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-gray-700">
                        {componentInfo.name}
                      </td>
                      {sortedOffers.map((offer) => {
                        const value = getPricingValue(offer, component);
                        return (
                          <td key={offer.id} className="py-3 px-4 text-center">
                            {value !== null ? (
                              <span className="font-mono text-sm">
                                {formatRate(value, componentInfo.unit)}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}

                {/* Green Energy */}
                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium text-gray-700 flex items-center gap-2">
                    <Leaf className="w-4 h-4 text-green-600" />
                    Green Energy
                  </td>
                  {sortedOffers.map((offer) => (
                    <td key={offer.id} className="py-3 px-4 text-center">
                      <span className="font-mono text-sm">
                        {offer.greenPercent}%
                      </span>
                    </td>
                  ))}
                </tr>

                {/* Estimated Annual Cost */}
                <tr className="border-b border-gray-100 hover:bg-gray-50 bg-blue-50/50">
                  <td className="py-3 px-4 font-semibold text-gray-900 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-blue-600" />
                    Est. Annual Cost
                  </td>
                  {sortedOffers.map((offer) => (
                    <td key={offer.id} className="py-3 px-4 text-center">
                      <div className="space-y-1">
                        <div className="font-semibold text-gray-900">
                          {formatCurrency(offer.estimatedAnnualCost || 0)}
                        </div>
                        {offer.estimatedSavingsPercent !== undefined && (
                          <div className={`text-xs flex items-center justify-center gap-1 ${
                            offer.estimatedSavingsPercent > 0 
                              ? 'text-green-600' 
                              : 'text-red-600'
                          }`}>
                            {offer.estimatedSavingsPercent > 0 ? (
                              <TrendingUp className="w-3 h-3" />
                            ) : (
                              <TrendingDown className="w-3 h-3" />
                            )}
                            {formatPercent(offer.estimatedSavingsPercent)}
                          </div>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Contract Term */}
                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium text-gray-700 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-600" />
                    Contract Term
                  </td>
                  {sortedOffers.map((offer) => (
                    <td key={offer.id} className="py-3 px-4 text-center">
                      <span className="font-mono text-sm">
                        {formatTermLabel(offer.termMonths)}
                      </span>
                    </td>
                  ))}
                </tr>

                {/* Actions Row */}
                <tr className="bg-gray-50">
                  <td className="py-4 px-4 font-medium text-gray-700">
                    Actions
                  </td>
                  {sortedOffers.map((offer) => (
                    <td key={offer.id} className="py-4 px-4 text-center">
                      <div className="space-y-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onOfferSelect(offer.id)}
                          className="w-full"
                        >
                          View Details
                        </Button>
                        {!offer.isRecommended && (
                          <Button
                            size="sm"
                            onClick={() => onSetRecommended(offer.id)}
                            className="w-full bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Star className="w-3 h-3 mr-1" />
                            Recommend
                          </Button>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile-friendly cards for smaller screens */}
          <div className="md:hidden space-y-4 mt-6">
            {sortedOffers.map((offer) => (
              <Card key={offer.id} className={`${
                offer.isRecommended ? 'border-green-300 bg-green-50' : ''
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">
                      {offer.retailerName}
                    </h3>
                    {offer.isRecommended && (
                      <Badge className="bg-green-100 text-green-800">
                        <Star className="w-3 h-3 mr-1 fill-current" />
                        BEST
                      </Badge>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-600">Annual Cost</p>
                      <p className="font-semibold">
                        {formatCurrency(offer.estimatedAnnualCost || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Savings</p>
                      <p className={`font-semibold ${
                        (offer.estimatedSavingsPercent || 0) > 0 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {formatPercent(offer.estimatedSavingsPercent || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Term</p>
                      <p className="font-semibold">{formatTermLabel(offer.termMonths)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Green Energy</p>
                      <p className="font-semibold">{offer.greenPercent}%</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onOfferSelect(offer.id)}
                      className="flex-1"
                    >
                      View Details
                    </Button>
                    {/* Recommendation button removed - now handled in Step 3 */}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OfferComparison;
